import { hasForegroundOrderForm } from '@/lib/order-form-guard'
import { Suspense, lazy, useEffect, useState } from 'react'
import { Alert, Button, Modal, Select, Spin, Tooltip, message } from 'antd'
import { useLocation, useNavigate, useBlocker } from 'react-router-dom'
import { orderDetailRoleContext, orderDetailTarget, orderRoleFromPath, orderViewRoles, safeOrderReturn } from '@/lib/order-role-context'
import { useConfirm } from '@/hooks/use-confirm'
import { isTabAccessibleForRole } from '@/lib/menu/utils'
import { menuConfig } from '@/lib/menu/config'
import { roleNameMap } from '@/stores/view-role'
import { Maximize2, Minimize2, X } from 'lucide-react'
import { useCurrentUser } from '@/hooks/use-auth'
import { useViewRoleStore } from '@/stores/view-role'
import { useOrderDetailModal } from '@/stores/order-detail-modal'
import { OrderDetailChromeProvider, OrderDetailState } from '@/components/order-detail/order-detail-layout'

// 懒加载:订单详情视图很大(~160KB),按需加载,不进各任务页/工作台的首屏包
const OrderDetailView = lazy(() =>
  import('@/components/order-detail-view').then((m) => ({ default: m.OrderDetailView })),
)

// Customer and scan/check entry points retain their own authorized data adapters.
const CustomerOrderDetailView = lazy(() => import('@/components/customer-order-detail-view').then(m => ({ default: m.CustomerOrderDetailView })))
const MonthlyOrderDetailView = lazy(() => import('@/components/monthly-order-detail-view').then(m => ({ default: m.MonthlyOrderDetailView })))
const OrderCheckView = lazy(() => import('@/pages/dashboard/my-check/order/[id]/index'))

/**
 * 全局订单详情弹窗 —— 一个「特殊的大号弹窗」:任务处理时查看订单详情,以大号居中模态
 * 盖在当前页上,不跳转页面、保住任务上下文。默认尺寸很大(近全屏),头部紧凑以最大化内容区。
 *
 * 复用项目既有的全屏 CSS 约定(`ant-modal-fullscreen` + `ant-modal-wrap-fullscreen`,见 globals.css)
 * + 内联样式方案(与 components/ant/ant-modal.tsx 一致)。不用 AntModal 是因为它是「标准尺寸/标准头部」,
 * 而订单详情需要专属的大尺寸 + 矮头部,改 AntModal 会波及全站其它弹窗。
 *
 * 同时全局拦截指向「订单详情页」的 <Link> 点击 —— 全站几十处订单入口无需逐个改,
 * 一律在此拦下改为开弹窗(react-router 的 Link 尊重 defaultPrevented,故 preventDefault 即可阻止跳页)。
 */

export function OrderDetailModal() {
  const { orderId, mode, role, open, close } = useOrderDetailModal()
  const [fullscreen, setFullscreen] = useState(false)
  const [localRole, setLocalRole] = useState<string | undefined>()
  // 订单摘要（订单号/状态/金额）由各角色视图渲染进标题栏，滚动时始终可见。
  const [summaryHost, setSummaryHost] = useState<HTMLDivElement | null>(null)
  const { data: user, isLoading, error } = useCurrentUser()
  const { viewRole } = useViewRoleStore()
  const location = useLocation()
  const navigate = useNavigate()
  const locationUrl = location.pathname + location.search + location.hash
  const actualRoles = (user?.roles || []) as string[]
  const requestedRole = localRole || role || viewRole
  // 来源页面和缓存只能选择本人实际拥有的角色；单角色账号始终使用自身身份。
  const { role: effectiveRole, switchRoles } = orderDetailRoleContext(actualRoles, requestedRole)
  const roles = orderViewRoles(actualRoles, effectiveRole)
  const monthly = effectiveRole === 'MONTHLY_ADMIN' || (user?.scope === 'CUSTOMER' && actualRoles.includes('MONTHLY_ADMIN') && mode !== 'customer' && effectiveRole !== 'CUSTOMER')
  const customer = mode === 'customer' || (roles.length === 1 && roles[0] === 'CUSTOMER')
  const { confirm, ConfirmDialog } = useConfirm()
  const blocker = useBlocker(({ currentLocation, nextLocation }) => !!orderId && currentLocation.key !== nextLocation.key && hasForegroundOrderForm())
  useEffect(() => {
    if (blocker.state === 'blocked') confirm({ title: '离开订单办理？', content: '当前还有打开的办理表单，离开会丢弃未提交的内容。',
      okText: '放弃并离开', cancelText: '继续填写', onOk: () => blocker.proceed(), onCancel: () => blocker.reset() })
  }, [blocker.state])
  const handleClose = () => {
    if (hasForegroundOrderForm()) { message.info('请先完成或关闭当前办理表单'); return }
    close(); setFullscreen(false); setLocalRole(undefined)
    if (orderDetailTarget(locationUrl, window.location.origin)) {
      const params = new URLSearchParams(location.search)
      const back = safeOrderReturn(params.get('returnTo'))
      const allowed = back && actualRoles.some((role: any) => isTabAccessibleForRole(back.split(/[?#]/)[0], role === 'ADMIN' ? null : role, menuConfig))
      navigate(allowed ? back : (customer ? '/dashboard/my-orders' : '/dashboard'), { replace: true })
    } else if (new URLSearchParams(location.search).has('orderDetail')) {
      const params = new URLSearchParams(location.search)
      for (const key of ['orderDetail', 'orderRole', 'orderMode']) params.delete(key)
      navigate(location.pathname + (params.size ? '?' + params.toString() : '') + location.hash, { replace: true })
    }
  }
  useEffect(() => { setLocalRole(undefined); setFullscreen(false) }, [orderId, mode, role, user?.id])
  useEffect(() => {
    const onUnload = (event: BeforeUnloadEvent) => { if (orderId && hasForegroundOrderForm()) { event.preventDefault(); event.returnValue = '' } }
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [orderId])
  useEffect(() => {
    const target = orderDetailTarget(locationUrl, window.location.origin)
    const params = new URLSearchParams(location.search)
    const restoredId = params.get('orderDetail')
    if (target) open(target.orderId, { ...target, sourceUrl: locationUrl })
    else if (restoredId && /^[a-zA-Z0-9_-]+$/.test(restoredId)) open(restoredId, {
      sourceUrl: locationUrl, role: params.get('orderRole') || orderRoleFromPath(location.pathname),
      mode: params.get('orderMode') === 'customer' ? 'customer' : params.get('orderMode') === 'check' ? 'check' : 'staff',
    })
    else if (useOrderDetailModal.getState().sourceUrl !== locationUrl) close()
  }, [locationUrl, open, close])
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const anchor = (event.target as HTMLElement | null)?.closest?.('a')
      if (!anchor || anchor.hasAttribute('download') || anchor.target === '_blank') return
      const target = orderDetailTarget(anchor.getAttribute('href') || '', window.location.origin)
      if (!target) return
      event.preventDefault()
      if (hasForegroundOrderForm()) { message.info('请先完成或关闭当前办理表单'); return }
      open(target.orderId, { ...target, sourceUrl: locationUrl, role: orderRoleFromPath(location.pathname) || viewRole || target.role })
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [open, locationUrl, viewRole])

  // 固定头部：订单摘要（未加载时显示「订单详情」）+ 视角切换 + 全屏切换 + 关闭
  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px 10px 20px', borderBottom: '1px solid #e2e8f0' }}>
      <div ref={setSummaryHost} data-order-summary className="peer min-w-0 flex-1 empty:hidden" />
      <span className="hidden flex-1 text-[15px] font-semibold text-slate-900 peer-empty:block">订单详情</span>
      {!customer && !monthly && mode !== 'check' && switchRoles.length > 1 && switchRoles.includes(effectiveRole) && <Select aria-label="订单详情角色视角" size="small" value={effectiveRole}
        style={{ minWidth: 150 }} onChange={next => { if (hasForegroundOrderForm()) { message.info('请先完成或关闭当前办理表单'); return }; setLocalRole(next) }}
        options={switchRoles.map(value => ({ value, label: (roleNameMap as Record<string, string>)[value] || value }))} />}
      <Tooltip title={fullscreen ? '恢复订单详情的常规查看大小' : '扩大订单详情查看区域'}>
      <Button
        type="text"
        size="small"
        aria-label={fullscreen ? '退出全屏' : '全屏'}
        title={fullscreen ? '退出全屏' : '全屏'}
        icon={fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
        onClick={() => setFullscreen((f) => !f)}
      />
      </Tooltip>
      <Tooltip title="关闭订单详情并返回原页面"><Button type="text" size="small" aria-label="关闭" icon={<X size={16} />} onClick={handleClose} /></Tooltip>
    </div>
  )

  return (<>
    <Modal
      open={!!orderId}
      onCancel={handleClose}
      footer={null}
      closable={false}
      centered={!fullscreen}
      title={header}
      width={fullscreen ? '100vw' : 'min(1440px, 96vw)'}
      className={fullscreen ? 'order-detail-modal ant-modal-fullscreen' : 'order-detail-modal'}
      wrapClassName={fullscreen ? 'ant-modal-wrap-fullscreen' : undefined}
      style={
        fullscreen
          ? { top: 0, left: 0, margin: 0, padding: 0, width: '100vw', maxWidth: '100vw', height: '100vh', position: 'absolute', display: 'flex', flexDirection: 'column' }
          : undefined
      }
      styles={{
        header: fullscreen ? { padding: 0, margin: 0, flex: '0 0 auto' } : { padding: 0, margin: 0 },
        // 主体固定高度、自身不滚动：页签主区与进度侧栏各自滚动，底部操作栏固定。
        body: fullscreen
          ? { flex: '1 1 0', minHeight: 0, maxHeight: 'none', overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }
          : { height: 'calc(min(92vh, 980px) - 64px)', overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' },
        container: { padding: 0, overflow: 'hidden' },
        wrapper: fullscreen ? { overflow: 'hidden', display: 'block', textAlign: 'left' } : undefined,
        mask: fullscreen ? { position: 'fixed', inset: 0 } : undefined,
      }}
      maskClosable={false}
      destroyOnHidden
    >
      {orderId && (
        <OrderDetailChromeProvider value={{ summaryHost }}>
        <Suspense fallback={<OrderDetailState><Spin /></OrderDetailState>}>
          {isLoading ? <OrderDetailState><Spin /></OrderDetailState> : error || !user ? <OrderDetailState><Alert type="error" message="无法确认当前用户，请刷新重试" /></OrderDetailState> :
            mode === 'check' ? <OrderCheckView orderId={orderId} /> : monthly ? <MonthlyOrderDetailView key={orderId} orderId={orderId} /> : customer ? <CustomerOrderDetailView key={orderId} orderId={orderId} /> :
            <OrderDetailView key={orderId} orderId={orderId} userRole={effectiveRole} />}
        </Suspense>
        </OrderDetailChromeProvider>
      )}
    </Modal><ConfirmDialog /></>
  )
}
